/**
 * Rekent één `PersonaPresenceReports`-document om naar aanwezige uren.
 *
 * WAAROM DIT BESTAAT: deze som stond drie keer los in de code — in
 * `PresenceTotals` (maandrooster), in `Presence` (deelnemerspagina) en in
 * `updatePersonaReports` (de nachtelijke functie). De drie waren het níet met
 * elkaar eens, en dat viel niemand op omdat elk scherm zijn eigen antwoord als
 * waarheid toont:
 *
 * - het maandrooster deed `report.CorrectedStart.split(':')` zonder te kijken
 *   of dat veld er wel is. Een document zonder correctievelden gooide daar een
 *   uitzondering, die werd opgevangen, en de dag kwam op **0.0 uur** te staan —
 *   terwijl de deelnemerspagina er 8 uur van maakte;
 * - alleen de nachtelijke functie kende een dienst die over middernacht heen
 *   loopt. In de twee schermen werd dat een negatief getal dat een totaal
 *   stilletjes verlaagt.
 *
 * De uren worden in minuten uitgerekend en niet met `new Date`: op de twee
 * dagen dat de klok verspringt zou een dag anders een uur te lang of te kort
 * zijn.
 *
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 */

/** 'HH:MM' — één of twee cijfers voor de dubbele punt, want '9:00' komt voor. */
const TIME_PATTERN = /^(\d{1,2}):(\d{2})$/

const MINUTES_PER_DAY = 24 * 60

/**
 * @param {*} value - Waarde uit een tijdveld (`Start`, `CorrectedEnd`, …)
 * @returns {number|null} Minuten sinds middernacht, of `null` als er geen
 *   bruikbare tijd in staat
 */
const toMinutes = (value) => {
    if (typeof value !== 'string') return null

    const match = TIME_PATTERN.exec(value.trim())
    if (!match) return null

    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours > 23 || minutes > 59) return null

    return hours * 60 + minutes
}

/**
 * @typedef {object} PresenceHours
 * @property {number} hours - Aanwezige uren; 0 bij een afwezigheid of bij
 *   tijden die niet te lezen zijn
 * @property {'correct'|'correction'|'absent'} type - Waarmee het scherm de cel
 *   kleurt en de legenda benoemt
 */

/**
 * @param {object|null|undefined} report - Eén document uit
 *   `PersonaPresenceReports`
 * @returns {PresenceHours}
 */
const presenceReportHours = (report) => {
    const data = report || {}

    if (data.Type === 'absent') return { hours: 0, type: 'absent' }

    const type = data.Type === 'correction' ? 'correction' : 'correct'

    // Een correctie geldt per kant: is alleen de eindtijd bijgesteld, dan blijft
    // de geroosterde begintijd staan.
    const start = toMinutes(data.CorrectedStart) ?? toMinutes(data.Start)
    const end = toMinutes(data.CorrectedEnd) ?? toMinutes(data.End)

    if (start === null || end === null) return { hours: 0, type }

    // Een eindtijd vóór de begintijd is een dienst die over middernacht heen
    // loopt, niet een fout — zo leest de nachtelijke functie het ook.
    const span = end < start ? end + MINUTES_PER_DAY - start : end - start

    return { hours: span / 60, type }
}

export default presenceReportHours
export { toMinutes }
